import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Input } from '@/shared/components/Input';
import { cn } from '@/core/utils/cn';
/**
 * Input de plata con formato AR.
 *
 * - **En blur**: muestra `1.234,56` (separador miles `.`, decimal `,`).
 * - **Al enfocar**: pasa al string crudo editable (`1234.56` o lo que el
 *   usuario haya tipeado), para no estorbar la edición.
 * - **`onChange(decimal)`**: siempre entrega decimal estándar `1234.56`,
 *   listo para backend / Zod regex `^\d+(\.\d{1,2})?$`.
 *
 * Este componente es **controlado** (no usa `register` de RHF).
 * Para integrarlo con RHF: `value={form.watch('campo')}` +
 * `onChange={(v) => form.setValue('campo', v, { shouldDirty: true })}`.
 */
export function MoneyInput({ value, onChange, className, error, onFocus, onBlur, ...rest }) {
    const [enfoque, setEnfoque] = useState(false);
    const [textoEditando, setTextoEditando] = useState('');
    const valorTexto = value == null || value === '' ? '' : String(value);
    const display = enfoque ? textoEditando : valorTexto ? formatearMiles(valorTexto) : '';
    return (_jsxs("div", { className: "relative", children: [_jsx("span", { className: "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 select-none text-sm font-medium text-text-tertiary", "aria-hidden": "true", children: "$" }), _jsx(Input, { type: "text", inputMode: "decimal", value: display, error: error, placeholder: "0,00", className: cn('num pl-7', className), onFocus: (e) => {
                    setEnfoque(true);
                    setTextoEditando(valorTexto);
                    // Selecciona todo para que el usuario pueda escribir directo.
                    requestAnimationFrame(() => e.target.select());
                    onFocus?.(e);
                }, onChange: (e) => {
                    const raw = e.target.value;
                    setTextoEditando(raw);
                    onChange(normalizarLive(raw));
                }, onBlur: (e) => {
                    setEnfoque(false);
                    onChange(normalizarFinal(textoEditando));
                    onBlur?.(e);
                }, ...rest })] }));
}
/**
 * Convierte cualquier formato razonable a decimal estándar `1234.56`,
 * preservando estados parciales mientras el usuario escribe (no fuerza
 * 2 decimales). Devuelve `''` si la cadena es inválida.
 */
function normalizarLive(raw) {
    if (raw == null)
        return '';
    let s = String(raw).trim().replace(/\$/g, '').replace(/\s/g, '');
    if (s === '')
        return '';
    if (s.includes(',') && s.includes('.')) {
        const ultimaComa = s.lastIndexOf(',');
        const ultimoPunto = s.lastIndexOf('.');
        if (ultimaComa > ultimoPunto) {
            // "1.234,56" → quitar puntos (miles), reemplazar coma por punto
            s = s.replace(/\./g, '').replace(',', '.');
        }
        else {
            // "1,234.56" → quitar comas (miles)
            s = s.replace(/,/g, '');
        }
    }
    else if (s.includes(',')) {
        // "1234,56" o "1234,5" o "1,234" — interpretamos coma como decimal
        // si hay una sola, o como miles si hay varias y ninguna decimal real.
        const cantComas = (s.match(/,/g) || []).length;
        if (cantComas === 1) {
            s = s.replace(',', '.');
        }
        else {
            s = s.replace(/,/g, '');
        }
    }
    else if (s.includes('.')) {
        // Heurística: si el "." aparece más de una vez es miles (ej. "1.234.567")
        // y lo limpiamos. Si aparece una sola y la parte fraccional es 3+
        // asumimos miles, sino decimal. Ej. "1.234" → "1234"; "1234.56" → "1234.56".
        const partes = s.split('.');
        if (partes.length === 2) {
            const entero = partes[0] ?? '';
            const fraccional = partes[1] ?? '';
            if (fraccional.length === 3) {
                // ambiguo "1.234" — interpretarlo como miles
                s = entero + fraccional;
            }
        }
        else {
            s = partes.join('');
        }
    }
    // Permitimos parciales: "", "1234", "1234.", "1234.5", "1234.56"
    if (!/^-?\d*(\.\d*)?$/.test(s))
        return '';
    return s;
}
function normalizarFinal(raw) {
    const live = normalizarLive(raw);
    if (!live || live === '-' || live === '.')
        return '';
    const num = Number(live);
    if (!Number.isFinite(num))
        return '';
    return num.toFixed(2);
}
/**
 * Formatea un string decimal estándar a formato AR con miles y dos
 * decimales: `1234.5` → `1.234,50`. Devuelve la cadena original si no
 * es numérica.
 */
function formatearMiles(decimal) {
    const num = Number(decimal);
    if (!Number.isFinite(num))
        return decimal;
    return num.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
